import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Whether the live-transcript dock is open, and who is showing it.
 *
 * Lives in a store rather than in CallHUD because the toggle and the dock are
 * no longer parent and child: the button sits in the call bar, while the dock
 * is a COLUMN of the dashboard shell, so the layout can give it real width
 * instead of floating it over the page.
 *
 * `claimedBy` is how a PAGE takes the transcript over. The service-call form is
 * the case: it is the screen you open because of what you are hearing, and the
 * transcript belongs beside the fields it is filling — not in a shell column
 * that would sit next to it showing the same call twice. A page claims on
 * mount, the shell dock renders nothing while the claim is held, and leaving
 * the page gives it back.
 *
 * Keyed by claimant rather than a boolean so a page unmounting after another
 * has already claimed cannot release a claim that is no longer its own.
 *
 * Deliberately NOT persisted. Showing the transcript is a per-call decision;
 * remembering it across reloads would spring a panel open on a session that
 * has no call in it.
 */
interface TranscriptDockState {
  open: boolean;
  claimedBy: string | null;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  claim: (owner: string) => void;
  release: (owner: string) => void;
}

export const useTranscriptDockStore = create<TranscriptDockState>()(
  devtools(
    (set) => ({
      open: false,
      claimedBy: null,
      toggle: () => set((state) => ({ open: !state.open }), false, 'transcript/toggle'),
      setOpen: (open) => set({ open }, false, 'transcript/setOpen'),
      claim: (owner) => set({ claimedBy: owner }, false, 'transcript/claim'),
      release: (owner) =>
        set(
          (state) => (state.claimedBy === owner ? { claimedBy: null } : state),
          false,
          'transcript/release',
        ),
    }),
    { name: 'transcript-dock' },
  ),
);

export const useTranscriptDockOpen = () => useTranscriptDockStore((state) => state.open);
export const useTranscriptClaimed = () =>
  useTranscriptDockStore((state) => state.claimedBy !== null);
